import React from 'react';
import { select } from 'd3-selection';
import { axisBottom, axisLeft, scaleBand, scaleLinear, scaleOrdinal } from 'd3';

import { IInventoryAnalysisChart } from '../../../interfaces/charts.interface';
import { NumberFormatter, NumberWithCommas } from '../../../helpers/Helpers';

interface Props {
  dataSource: IInventoryAnalysisChart[];
  component: string;
  period: string;
}

interface State {
  dataSource: IInventoryAnalysisChart[];
  chartData: any[];
}

export default class BarChart extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      dataSource: this.props.dataSource,
      chartData: this.getChartData(this.props.dataSource)
    };
  }

  componentDidMount(): void {
    this.draw();
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
    if (this.props.dataSource !== prevProps.dataSource) {
      this.setState({
        dataSource: this.props.dataSource,
        chartData: this.getChartData(this.props.dataSource)
      }, () => {
        select(`.${this.props.component}`).selectAll('*').remove();
        this.draw();
      });
    }
  }

  getChartData(dataSource) {
    const items = dataSource
      .filter((data) => data.checked)
      .reduce((acc: any[], group) => {
        const list = group.children.map((subgroup) => {
          return {
            ...subgroup,
            group: group.name,
            values: subgroup.values.map((value) => {
              return {
                ...value,
                group: group.name,
                measure: subgroup.name
              };
            })
          };
        });
        acc.push(list);
        return acc;
      }, []);

    return [].concat.apply([], items);
  }

  draw(): void {
    if (this.state.dataSource.length === 0) {
      return;
    }

    // find the max range of y-axis
    const yMax = this.state.chartData.reduce((acc, group) => {
      const max = group.values.reduce((init, subgroup) => init > subgroup.value ? init : subgroup.value, 0);
      return acc > max ? acc : max;
    }, 0);
    const yMin = this.state.chartData.reduce((acc, group) => {
      const min = group.values.reduce((init, subgroup) => init < subgroup.value ? init : subgroup.value, 0);
      return acc < min ? acc : min;
    }, 0);

    // set the dimensions and margins of the graph
    const margin = {top: 20, right: 30, bottom: 120, left: 80},
      width = 1000 - margin.left - margin.right,
      height = 400 - margin.top - margin.bottom;

    // append the svg object to the body of the page
    const svg = select(`.${this.props.component}`)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // List of groups
    const groups: string[] = this.state.dataSource[0].children.map((d) => d.name);

    // List of subgroups
    const subgroups: string[] = this.state.dataSource.reduce((acc: any[], group) => {
      const list = group.children[0].values
        .filter((subgroup) => subgroup.checked)
        .map((subgroup) => subgroup.name);
      acc.push(list);
      return [].concat.apply([], acc);
    }, []);

    // List of colorList
    const colorList: string[] = this.state.dataSource.reduce((acc: any[], group) => {
      const list = group.children[0].values
        .filter((subgroup) => subgroup.checked)
        .map((subgroup) => subgroup.color);
      acc.push(list);
      return [].concat.apply([], acc);
    }, []);

    // Add X axis
    const x = scaleBand()
      .domain(groups)
      .range([0, width])
      .padding(0.2);

    svg.append('g')
      .attr('transform', 'translate(0,' + height + ')')
      .call(axisBottom(x))
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('transform', 'rotate(-45)');

    // Add Y axis
    const y = scaleLinear()
      .domain([yMin, yMax])
      .range([height, 0]);

    /*Add a darker line on y-axis 0 value*/
    const yZero = y(0);
    svg.append('line')
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', yZero)
      .attr('y2', yZero)
      .style('stroke', '#000000');

    // const formatValue = format('~s');

    // Add y-axis grid lines
    svg.append('g')
      .attr('class', 'grid')
      .call(axisLeft(y)
        .tickSize(-width)
        .tickFormat((d: any) => NumberFormatter(d))
        .tickSizeOuter(0));

    // Another scale for subgroup position
    const xSubgroup: any = scaleBand()
      .domain(subgroups)
      .range([0, x.bandwidth()])
      .padding(0.05);

    // color palette = one color per subgroup
    const color: any = scaleOrdinal()
      .domain(subgroups)
      .range(colorList);

    // create a tooltip
    const tooltip = select(`.${this.props.component}`)
      .append('div')
      .style('opacity', 0)
      .attr('class', 'tooltip');

    // Three function that change the tooltip when user hover / move / leave a cell
    const mouseover = () => {
      tooltip
        .style('opacity', 1);
    };
    const mousemove = (event, d) => {
      tooltip
        .html(d.measure + '<br/>' + 'Value: ' + NumberWithCommas(d.value))
        .style('left', `${event.layerX + 10}px`)
        .style('top', `${event.layerY}px`);
    };
    const mouseleave = () => {
      tooltip
        .style('opacity', 0);
    };

    // Show the angle strip bars
    const hatchSize = 3;

    this.state.dataSource
      .filter((group) => ['REVENUE', 'MARGINS'].indexOf(group.name) !== -1)
      .forEach((group) => {
        group.children[0].values.forEach((subgroup, idx: number) => {
          svg
            .append('pattern')
            .attr('id', `hatch${group.name}${idx}`)
            .attr('width', hatchSize)
            .attr('height', hatchSize)
            .attr('patternTransform', `rotate(${group.name === 'REVENUE' ? 45 : 90})`)
            .attr('patternUnits', 'userSpaceOnUse')
            .append('rect')
            .attr('width', hatchSize / 2)
            .attr('height', hatchSize)
            .style('fill', subgroup.color);
        });
      });

    // Show the bars
    svg.append('g')
      .selectAll('g')
      // Enter in data = loop group per group
      .data(this.state.chartData)
      .enter()
      .append('g')
      .attr('transform', (d) => `translate(${x(d.name)}, 0)`)
      .selectAll('rect')
      .data((d) => d.values.map((subgroup) => {
        return {key: subgroup.name, value: subgroup.value, group: subgroup.group, measure: subgroup.name};
      }))
      .enter().append('rect')
      .attr('x', (d: any) => xSubgroup(d.key))
      // .attr('y', (d: any) => y(d.value))
      .attr('y', (d: any) => y(Math.max(0, d.value)))
      .attr('width', xSubgroup.bandwidth())
      // .attr('height', (d: any) => height - y(d.value))
      .attr('height', (d: any) => Math.abs(y(d.value) - y(0)))
      .attr('fill', (d: any, idx: number) =>
        d.group === 'UNITS'
          ? color(d.key)
          : `url(#hatch${d.group}${idx})`)
      .on('mouseover', mouseover)
      .on('mousemove', mousemove)
      .on('mouseleave', mouseleave);

    // Add x-axis label
    svg.append('text')
      .attr('class', 'x-axis-label')
      .attr('x', width / 2)
      .attr('y', height + 80)
      .text('Period');
  }

  render(): React.ReactNode {
    return (
      <div
        className={`${this.props.component}`}
        style={{overflowX: 'scroll'}}
      />
    );
  }

}
